import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13 } from "styled-components";
import React from "react";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { takeFirst } from "~/features/app/utils";
import { isLinkValid } from "~/features/ui/link/utils";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { Headline } from "~/features/ui/headline";
import { Grid } from "~/features/ui/grid";
import { ChipEvolutionGraphBlockContext } from "~/features/blocks/inventory/chip-evolution-graph/context";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type ChipEvolutionGraphBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  bulletPoints?: React.ReactNode;
  hasGraph?: boolean;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  linkText?: string | null;
  mode?: string | null;
  renderGraph?: () => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};
export function ChipEvolutionGraphBlockView({
  anchor,
  bulletPoints,
  hasGraph = false,
  hasTopDivider = true,
  headline,
  link: linkValue,
  linkText,
  mode = "light",
  renderGraph,
  sectionProps,
  text,
  theme = "white"
}: ChipEvolutionGraphBlockViewProps) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const cta = takeFirst(linkValue);
  const icon = getPlainLinkIcon(cta);
  const renderFooter = isLinkValid(cta) || Boolean(linkText);
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.background, themeStyle.text, themeStyle.border]}>
      <Wrap mode={mode as Mode}>
        <ChipEvolutionGraphBlockContext.Provider value={{
        theme: theme as Theme,
        mode: mode as Mode
      }}>
          <_StyledDiv>
            <_StyledDiv2 $_css2={[{
            "position": "relative",
            "zIndex": "10",
            "display": "flex",
            "flexDirection": "column",
            "> :not([hidden]) ~ :not([hidden])": {
              "--tw-divide-y-reverse": "0",
              "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
              "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
            },
            "@media (min-width: 1024px)": {
              "borderLeftWidth": "1px",
              "borderRightWidth": "1px"
            }
          }, themeStyle.border, themeStyle.background]}>
              <_StyledGrid $_css3={[{
              "> :not([hidden]) ~ :not([hidden])": {
                "--tw-divide-y-reverse": "0",
                "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
                "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
              },
              "@media (min-width: 1024px)": {
                "> :not([hidden]) ~ :not([hidden])": {
                  "--tw-divide-x-reverse": "0",
                  "borderRightWidth": "calc(1px * var(--tw-divide-x-reverse))",
                  "borderLeftWidth": "calc(1px * calc(1 - var(--tw-divide-x-reverse)))",
                  "--tw-divide-y-reverse": "0",
                  "borderTopWidth": "calc(0px * calc(1 - var(--tw-divide-y-reverse)))",
                  "borderBottomWidth": "calc(0px * var(--tw-divide-y-reverse))"
                }
              }
            }, themeStyle.border]}>
                <_StyledDiv3 $_css4={[{
                "gridColumn": "1 / -1",
                "display": "flex",
                "flexDirection": "column",
                "> :not([hidden]) ~ :not([hidden])": {
                  "--tw-divide-y-reverse": "0",
                  "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
                  "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
                },
                "@media (min-width: 1024px)": {
                  "gridColumn": "span 4 / span 4",
                  "display": "flex",
                  "flexDirection": "column",
                  "justifyContent": "space-between"
                }
              }, themeStyle.border]}>
                  <_StyledDiv4>
                    {headline && <_StyledHeadline text={headline} />}
                    {text}
                  </_StyledDiv4>
                  {hasGraph && <_StyledDiv5>{renderGraph?.()}</_StyledDiv5>}
                  {bulletPoints}
                </_StyledDiv3>
                {hasGraph && <_StyledDiv6>{renderGraph?.()}</_StyledDiv6>}
              </_StyledGrid>
              {renderFooter && <_StyledDiv7>
                  <_StyledDiv8 $_css5={[{
                "borderLeftWidth": "1px",
                "borderRightWidth": "1px",
                "padding": "2.4rem",
                "@media (min-width: 1024px)": {
                  "display": "flex",
                  "justifyContent": "space-between",
                  "borderLeftWidth": "0px",
                  "borderRightWidth": "0px",
                  "padding": "4rem"
                }
              }, themeStyle.border]}>
                    {linkText && <_StyledP>{linkText}</_StyledP>}
                    {isLinkValid(cta) && <_StyledDiv9>
                        <Button as={Link} {...cta} icon={icon} variant="underline" />
                      </_StyledDiv9>}
                  </_StyledDiv8>
                </_StyledDiv7>}
            </_StyledDiv2>
          </_StyledDiv>
        </ChipEvolutionGraphBlockContext.Provider>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-yvs30v-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-yvs30v-1"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-yvs30v-2"
})`${p => p.$_css2}`;
var _StyledGrid = _styled4(Grid).withConfig({
  componentId: "sc-sf-yvs30v-3"
})`${p => p.$_css3}`;
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-yvs30v-4"
})`${p => p.$_css4}`;
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-yvs30v-5"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "2.4rem",
  "padding": "4rem"
});
var _StyledHeadline = _styled7(Headline).withConfig({
  componentId: "sc-sf-yvs30v-6"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.2rem",
    "lineHeight": "1.2",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledDiv5 = _styled8("div").withConfig({
  componentId: "sc-sf-yvs30v-7"
})({
  "gridColumn": "1 / -1",
  "display": "block",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledDiv6 = _styled9("div").withConfig({
  componentId: "sc-sf-yvs30v-8"
})({
  "gridColumn": "1 / -1",
  "display": "none",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 8 / span 8",
    "display": "block"
  }
});
var _StyledDiv7 = _styled10("div").withConfig({
  componentId: "sc-sf-yvs30v-9"
})({
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "@media (min-width: 1024px)": {
    "paddingLeft": "0",
    "paddingRight": "0"
  }
});
var _StyledDiv8 = _styled11("div").withConfig({
  componentId: "sc-sf-yvs30v-10"
})`${p => p.$_css5}`;
var _StyledP = _styled12("p").withConfig({
  componentId: "sc-sf-yvs30v-11"
})({
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledDiv9 = /*#__PURE__*/_styled13("div").withConfig({
  displayName: "chip-evolution-graph___StyledDiv9",
  componentId: "sc-13ha83a-0"
})({
  "marginTop": "2.4rem",
  "@media (min-width: 1024px)": {
    "marginTop": "0"
  }
});